import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
    AskStanleyActions,
    AskStanleyState,
    ChatMessage,
    ChatSession,
    ProductData,
} from '@/features/ask-stanley/types/askStanley.types';
import { ASK_STANLEY_CONSTANTS } from '@/features/ask-stanley/constants/askStanley.constants';
import Cookies from 'js-cookie';

const in10Minutes = 1 / 24 / 6; // js-cookies uses days as base unit

// Safely parse the generated page data cookie
const getInitialGeneratedPageData = (): ProductData | null => {
    try {
        const cookieValue = Cookies.get('generated_page_data');
        if (cookieValue && cookieValue !== 'undefined' && cookieValue !== 'null') {
            return JSON.parse(cookieValue);
        }
        return null;
    } catch {
        return null;
    }
};

interface AskStanleyStore extends AskStanleyState, AskStanleyActions {
    // Pure state setters
    setStatus: (status: string) => void;
    setError: (error: string | null) => void;
    setChats: (chats: ChatSession[]) => void;
    setCurrentChatId: (chatId: string | number) => void;
    setGeneratedPageData: (product: ProductData | undefined) => void;

    // Pure state management methods
    addMessage: (chatId: string | number, message: ChatMessage) => void;
    updateMessage: (chatId: string | number, messageId: string | number, updates: Partial<ChatMessage>) => void;
    updateMessageId: (chatId: string | number, oldMessageId: string | number, newMessageId: string | number) => void;
    updateChatId: (oldChatId: string | number, newChatId: string | number) => void;
    updateChatTitle: (chatId: string | number, title: string) => void;
    setLoading: (chatId: string | number, loading: boolean) => void;
    setStreaming: (chatId: string | number, streaming: boolean) => void;
    setAbortController: (chatId: string | number, controller: AbortController | null) => void;
    updateMessageFeedback: (chatId: string | number, messageId: string | number, feedback: number) => void;
    removeMessage: (chatId: string | number, messageId: string | number) => void;

    // Helper functions
    generateThreadName: (message: string) => string;
}

export const useAskStanleyStore = create<AskStanleyStore>()(
    devtools(
        (set, get) => ({
            // State
            isOpen: false,
            currentChatId: -1,
            chats: [
                {
                    id: -1,
                    title: ASK_STANLEY_CONSTANTS.DEFAULT_CHAT_TITLE,
                    messages: [],
                    loading: false,
                    isStreaming: false,
                    abortController: null,
                    created_at: new Date(),
                    updated_at: new Date(),
                },
            ],
            status: 'Thinking...',
            error: null,
            generatedPageData: getInitialGeneratedPageData(),
            isLoading: false,
            isStreaming: false,

            // Pure state setters
            setStatus: (status: string) => set({ status }),
            setError: (error: string | null) => set({ error }),
            setChats: (chats: ChatSession[]) => set({ chats }),
            setCurrentChatId: (currentChatId: string | number) => set({ currentChatId }),
            setGeneratedPageData: (product: ProductData | null) => {
                set({ generatedPageData: product });
                Cookies.set('generated_page_data', JSON.stringify(product), { expires: in10Minutes });
                Cookies.set('generated_page_data', JSON.stringify(product), {
                    domain: 'stan.store',
                    expires: in10Minutes,
                });
                Cookies.set('generated_page_data', JSON.stringify(product), {
                    domain: 'stanwith.me',
                    expires: in10Minutes,
                });
            },

            // Helper functions
            generateThreadName: (message: string): string => {
                const cleanMessage = message
                    .trim()
                    .replace(/[^\w\s]/g, '')
                    .substring(0, 50);
                return cleanMessage || ASK_STANLEY_CONSTANTS.DEFAULT_CHAT_TITLE;
            },

            // Pure state management methods
            addMessage: (chatId: string | number, message: ChatMessage) => {
                set(state => ({
                    chats: state.chats.map(chat =>
                        chat.id === chatId ? { ...chat, messages: [...chat.messages, message] } : chat
                    ),
                }));
            },

            updateMessage: (chatId: string | number, messageId: string | number, updates: Partial<ChatMessage>) => {
                set(state => ({
                    chats: state.chats.map(chat => {
                        if (chat.id === chatId) {
                            return {
                                ...chat,
                                messages: chat.messages.map(msg =>
                                    msg.id === messageId ? { ...msg, ...updates } : msg
                                ),
                            };
                        }
                        return chat;
                    }),
                }));
            },

            updateMessageId: (
                chatId: string | number,
                oldMessageId: string | number,
                newMessageId: string | number
            ) => {
                set(state => ({
                    chats: state.chats.map(chat => {
                        if (chat.id === chatId) {
                            return {
                                ...chat,
                                messages: chat.messages.map(msg =>
                                    msg.id === oldMessageId ? { ...msg, id: newMessageId } : msg
                                ),
                            };
                        }
                        return chat;
                    }),
                }));
            },

            updateChatId: (oldChatId: string | number, newChatId: string | number) => {
                set(state => ({
                    chats: state.chats.map(chat => (chat.id === oldChatId ? { ...chat, id: newChatId } : chat)),
                    currentChatId: state.currentChatId === oldChatId ? newChatId : state.currentChatId,
                }));
            },

            updateChatTitle: (chatId: string | number, title: string) => {
                set(state => ({
                    chats: state.chats.map(chat => (chat.id === chatId ? { ...chat, title } : chat)),
                }));
            },

            setLoading: (chatId: string | number, loading: boolean) => {
                set(state => ({
                    chats: state.chats.map(chat => (chat.id === chatId ? { ...chat, loading } : chat)),
                }));
            },

            setStreaming: (chatId: string | number, streaming: boolean) => {
                set(state => ({
                    chats: state.chats.map(chat => (chat.id === chatId ? { ...chat, isStreaming: streaming } : chat)),
                }));
            },

            setAbortController: (chatId: string | number, controller: AbortController | null) => {
                set(state => ({
                    chats: state.chats.map(chat =>
                        chat.id === chatId ? { ...chat, abortController: controller } : chat
                    ),
                }));
            },

            updateMessageFeedback: (chatId: string | number, messageId: string | number, feedback: number) => {
                set(state => ({
                    chats: state.chats.map(chat => {
                        if (chat.id === chatId) {
                            return {
                                ...chat,
                                messages: chat.messages.map(msg =>
                                    msg.id === messageId ? { ...msg, feedback_status: feedback } : msg
                                ),
                            };
                        }
                        return chat;
                    }),
                }));
            },

            removeMessage: (chatId: string | number, messageId: string | number) => {
                set(state => ({
                    chats: state.chats.map(chat => {
                        if (chat.id === chatId) {
                            return {
                                ...chat,
                                messages: chat.messages.filter(msg => msg.id !== messageId),
                            };
                        }
                        return chat;
                    }),
                }));
            },

            // Public actions (implemented in hooks)
            openChat: () => {
                set({ isOpen: true });
            },

            closeChat: () => {
                set({ isOpen: false });
            },

            createNewChat: () => {
                const newChatId = Date.now() * -1;
                const newChat: ChatSession = {
                    id: newChatId,
                    title: ASK_STANLEY_CONSTANTS.NEW_CHAT_TITLE,
                    messages: [],
                    loading: false,
                    isStreaming: false,
                    abortController: null,
                    created_at: new Date(),
                    updated_at: new Date(),
                };

                set(state => ({
                    chats: [...state.chats, newChat],
                    currentChatId: newChatId,
                }));
            },

            switchToChat: (chatId: string | number) => {
                set({ currentChatId: chatId });
            },

            // These will be implemented in the hook using logic files
            sendMessage: async () => {
                throw new Error('sendMessage should be called through useAskStanley hook');
            },

            refreshMessage: () => {
                throw new Error('refreshMessage should be called through useAskStanley hook');
            },

            giveFeedback: async () => {
                throw new Error('giveFeedback should be called through useAskStanley hook');
            },

            copyMessage: async () => {
                throw new Error('copyMessage should be called through useAskStanley hook');
            },

            abortCurrentRequest: () => {
                const currentChatId = get().currentChatId;
                const currentChat = get().chats.find(chat => chat.id === currentChatId);

                if (!currentChat) return;

                // Abort the request first
                if (currentChat.abortController) {
                    currentChat.abortController.abort();
                }

                // Clean up placeholder messages and status messages BEFORE resetting flags
                const statusMessageId = ASK_STANLEY_CONSTANTS.MESSAGE_IDS.CURRENT_STATUS;
                const messagesToRemove: (string | number)[] = [];

                // Collect status messages to remove
                currentChat.messages.forEach(message => {
                    if (
                        message.type === ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.STATUS ||
                        message.id === statusMessageId
                    ) {
                        messagesToRemove.push(message.id);
                    }
                });

                // Remove all collected messages
                messagesToRemove.forEach(messageId => {
                    get().removeMessage(currentChatId, messageId);
                });

                // Always reset UI-blocking flags so the input becomes usable again
                get().setLoading(currentChatId, false);
                get().setStreaming(currentChatId, false);
                get().setAbortController(currentChatId, null);
            },
        }),
        { name: 'AskStanleyStore' }
    )
);
